<template>
  <section class="content__summary">
    <p class="title">{{ $t('common.summary') }}</p>
    <!-- graph -->
    <div class="box-layout box__graph">
      <ul class="button--wrap">
        <li v-for="item in chartItems" :key="item.key"
            @click="currentChart = item.key"
            :class="{on: currentChart === item.key}">
          {{ item.text }}
        </li>
      </ul>
      <scroll-content class="graph--wrap">
        <AppChart v-if="chartOptions"
          chartType="line"
          :height="170"
          :chartOptions="chartOptions" />
      </scroll-content>
    </div>

    <!-- values -->
    <div class="box__indicator">
      <div class="box-layout" v-for="abType in abTypes" :key="abType">
        <div class="label style1" :class="abType === 'a' ? 'style1' : 'style2'">
          <span class="emphasis">{{ abType | upperFirst }}</span>
        </div>
        <ul class="indicator--wrap">
          <li :class="summary[SUMMARY_TYPE.pv].compare">
            <span>
              <ui-icon-tooltip iconSize="small" arrowLeft>
                <span v-html="$t('common.reportIndicator.pageViews.tooltip')"></span>
              </ui-icon-tooltip>
              {{ $t('common.reportIndicator.pageViews.label') }}
            </span>
            <span class="value" :class="`${abType}Value`">{{ summary[SUMMARY_TYPE.pv][abType] }} PV</span>
          </li>

          <li :class="summary[SUMMARY_TYPE.conversion].compare">
            <span>
              <ui-icon-tooltip iconSize="small" arrowLeft>
                <span v-html="$t('common.reportIndicator.conversionRate.tooltip')"></span>
              </ui-icon-tooltip>
              {{ $t('common.reportIndicator.conversionRate.label') }}
            </span>
            <span class="value" :class="`${abType}Value`">
            {{ summary[SUMMARY_TYPE.conversion][abType] }} %
          </span>
          </li>

          <li :class="summary[SUMMARY_TYPE.clickPerPv].compare">
            <span>
              <ui-icon-tooltip iconSize="small" arrowLeft>
                <span v-html="$t('common.reportIndicator.clicksPerPv.tooltip')"></span>
              </ui-icon-tooltip>
              {{ $t('common.reportIndicator.clicksPerPv.label') }}
            </span>
            <span class="value" :class="`${abType}Value`">{{ summary[SUMMARY_TYPE.clickPerPv][abType] | clicks }}</span>
          </li>

        </ul>
        <i class="divide"></i>
        <ul class="indicator--wrap">
          <li :class="summary[SUMMARY_TYPE.clickPvRate].compare">
            <span>
              <ui-icon-tooltip iconSize="small" arrowLeft>
                <span v-html="$t('common.reportIndicator.clickPvRate.tooltip')"></span>
              </ui-icon-tooltip>
              {{ $t('common.reportIndicator.clickPvRate.label') }}
            </span>
            <span class="value" :class="`${abType}Value`">{{ summary[SUMMARY_TYPE.clickPvRate][abType] }} %</span>
          </li>
          <li :class="summary[SUMMARY_TYPE.exitRate].compare">
            <span>
              <ui-icon-tooltip iconSize="small" arrowLeft>
                <span v-html="$t('common.reportIndicator.exitRate.tooltip')"></span>
              </ui-icon-tooltip>
              {{ $t('common.reportIndicator.exitRate.label') }}
            </span>
            <span class="value" :class="`${abType}Value`">{{ summary[SUMMARY_TYPE.exitRate][abType] }} %</span>
          </li>
          <li :class="summary[SUMMARY_TYPE.avgDuration].compare">
            <span>
              <ui-icon-tooltip iconSize="small" arrowLeft>
                <span v-html="$t('common.reportIndicator.avgDuration.tooltip')"></span>
              </ui-icon-tooltip>
              {{ $t('common.reportIndicator.avgDuration.label') }}
            </span>
            <span class="value" :class="`${abType}Value`">{{ summary[SUMMARY_TYPE.avgDuration][abType] }} s</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
import { parse, format, addDays } from 'date-fns'
import { getPercentage, sum } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import AppChart from '@/components/chart'
import ScrollContent from '@/components/ui/ScrollContent'
import UiIconTooltip from '@/components/ui/UiIconTooltip'

const SUMMARY_TYPE = {
  pv: 'pv',
  conversion: 'conversion',
  clickPerPv: 'clickPerPv',
  clickPvRate: 'clickPvRate',
  exitRate: 'exitRate',
  avgDuration: 'avgDuration'
}

export default {
  name: 'ABSummary',
  components: {
    AppChart,
    ScrollContent,
    UiIconTooltip
  },
  props: {
    abTypes: {
      type: Array,
      required: true
    },
    aAnalyticsData: {
      type: Object,
      required: true
    },
    bAnalyticsData: {
      type: Object,
      required: true
    },
    aHeatmapData: {
      type: Object,
      required: true
    },
    bHeatmapData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      currentChart: null
    }
  },
  computed: {
    pageLabel () {
      return this.$t('common.page')
    },
    chartItems () {
      return [
        {
          key: SUMMARY_TYPE.conversion,
          text: 'Click Conversion Rate'
        },
        {
          key: SUMMARY_TYPE.clickPerPv,
          text: 'Click per PV'
        },
        {
          key: SUMMARY_TYPE.clickPvRate,
          text: 'Click PV Rate'
        },
        {
          key: SUMMARY_TYPE.exitRate,
          text: 'Click Exit Rate'
        },
        {
          key: SUMMARY_TYPE.avgDuration,
          text: 'Avg Duration'
        }
      ]
    },
    summary () {
      const data = {
        [SUMMARY_TYPE.pv]: this.getPvData(),
        [SUMMARY_TYPE.conversion]: this.getConversionData(),
        [SUMMARY_TYPE.clickPerPv]: this.getClickPerPvData(),
        [SUMMARY_TYPE.clickPvRate]: this.getClickPvRateData(),
        [SUMMARY_TYPE.exitRate]: this.getExitRateData(),
        [SUMMARY_TYPE.avgDuration]: this.getAvgDurationData()
      }
      return data
    },
    aPvTotal () {
      return this.aAnalyticsData.pvTotal
    },
    bPvTotal () {
      return this.bAnalyticsData.pvTotal
    },
    chartData () {
      switch (this.currentChart) {
        case SUMMARY_TYPE.conversion:
          return this.getConversionChartData()
        case SUMMARY_TYPE.clickPerPv:
          return this.getClickPerPvChartData()
        case SUMMARY_TYPE.clickPvRate:
          return this.getClickPvRateChartData()
        case SUMMARY_TYPE.exitRate:
          return this.getExitRateChartData()
        case SUMMARY_TYPE.avgDuration:
          return this.getAvgDurationChartData()
        default:
          return null
      }
    },
    chartOptions () {
      if (!this.aAnalyticsData) {
        return null
      }
      const currentChart = this.currentChart
      const chartData = this.chartData
      const xData = this.aAnalyticsData.daily.map(item => format(parse(item.day), 'YYYY-MM-DD'))
      const aData = chartData.a
      const bData = chartData.b

      if (xData.length === 1) {
        const prevDate = addDays(parse(xData[0]), -1)
        xData.unshift(format(prevDate, 'YYYY-MM-DD'))
        aData.unshift(0)
        bData.unshift(0)
      }

      const clicksFilter = this.$options.filters.clicks

      const options = {
        grid: {
          containLabel: true,
          right: 30,
          left: 20,
          top: 30,
          bottom: 0
        },
        series: [
          {
            name: `A ${this.pageLabel}`,
            type: 'line',
            data: aData,
            showSymbol: false,
            symbolSize: 2,
            smooth: true
          },
          {
            name: `B ${this.pageLabel}`,
            type: 'line',
            data: bData,
            showSymbol: false,
            symbolSize: 2,
            smooth: true
          }
        ],
        xAxis: {
          type: 'category',
          data: xData,
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          splitNumber: 2,
          axisLabel: {}
        },
        tooltip: {
          formatter (params) {
            const title = format(parse(params[0].axisValue), 'MM-DD')
            const content = []

            for (let i = 0; i < params.length; i++) {
              const param = params[i]
              let value = param.value

              switch (currentChart) {
                case SUMMARY_TYPE.conversion:
                case SUMMARY_TYPE.clickPvRate:
                case SUMMARY_TYPE.exitRate:
                  value = `${value}%`
                  break
                case SUMMARY_TYPE.clickPerPv:
                  value = `${clicksFilter(value)}`
                  break
                case SUMMARY_TYPE.avgDuration:
                  value = `${value}s`
                  break
              }
              content.push([
                `${param.marker}${param.seriesName} :`,
                value
              ])
            }
            return getTooltipHtml(content, title)
          }
        },
        color: ['#9fce12', '#43b6c9']

      }
      return options
    }
  },
  created () {
    this.SUMMARY_TYPE = SUMMARY_TYPE
    this.currentChart = this.chartItems[0].key
  },
  mounted () {
    this.getSummary()
  },
  methods: {
    getSummary () {
      const data = {
        [SUMMARY_TYPE.pv]: this.getPvData(),
        [SUMMARY_TYPE.conversion]: this.getConversionData(),
        [SUMMARY_TYPE.clickPerPv]: this.getClickPerPvData(),
        [SUMMARY_TYPE.clickPvRate]: this.getClickPvRateData(),
        [SUMMARY_TYPE.exitRate]: this.getExitRateData(),
        [SUMMARY_TYPE.avgDuration]: this.getAvgDurationData()
      }
      this.$emit('getSummary', data)
    },
    getCompareData (aValue, bValue) {
      const data = {
        a: isFinite(aValue) ? aValue : 0,
        b: isFinite(bValue) ? bValue : 0,
        compare: ''
      }

      if (data.a > data.b) {
        data.compare = 'largeA'
      } else if (data.a < data.b) {
        data.compare = 'largeB'
      }

      return data
    },
    getPvData () {
      return this.getCompareData(this.aPvTotal, this.bPvTotal)
    },
    getConversionData () {
      const aValue = getPercentage(this.aAnalyticsData.cvrExist, this.aPvTotal, 1)
      const bValue = getPercentage(this.bAnalyticsData.cvrExist, this.bPvTotal, 1)
      return this.getCompareData(aValue, bValue)
    },
    getConversionChartData () {
      const mapFn = (item) => {
        return getPercentage(item.cvr, item.pv, 1)
      }
      return {
        a: this.aAnalyticsData.daily.map(mapFn),
        b: this.bAnalyticsData.daily.map(mapFn)
      }
    },
    getClickPerPvData () {
      let aValue = 0
      let bValue = 0

      if (this.aHeatmapData.metric) {
        if (this.aHeatmapData.metric.tabCount) {
          aValue = Number((this.aHeatmapData.metric.tabCount / this.aPvTotal).toFixed(2))
        } else {
          aValue = Number((this.aHeatmapData.metric.clickCount / this.aPvTotal).toFixed(2))
        }
      }
      if (this.bHeatmapData.metric) {
        if (this.bHeatmapData.metric.tabCount) {
          bValue = Number((this.bHeatmapData.metric.tabCount / this.aPvTotal).toFixed(2))
        } else {
          bValue = Number((this.bHeatmapData.metric.clickCount / this.aPvTotal).toFixed(2))
        }
      }
      return this.getCompareData(aValue, bValue)
    },
    getClickPerPvChartData () {
      const mapFn = (item, dailyClicks) => {
        const dateStr = format(parse(item.day), 'YYYY-MM-DD')
        if (!item.pv || !dailyClicks[dateStr]) {
          return 0
        }
        // todo: check calculation
        return Math.round(dailyClicks[dateStr] / item.pv * 10) / 10
      }
      const aDailyClicks = this.aHeatmapData.dailyClicks ? this.aHeatmapData.dailyClicks : this.aHeatmapData.dailyTabs
      const bDailyClicks = this.bHeatmapData.dailyClicks ? this.bHeatmapData.dailyClicks : this.bHeatmapData.dailyTabs

      return {
        a: this.aAnalyticsData.daily.map(item => mapFn(item, aDailyClicks)),
        b: this.bAnalyticsData.daily.map(item => mapFn(item, bDailyClicks))
      }
    },
    getClickPvRateData () {
      const aPv = sum(this.aAnalyticsData.daily, 'clickPv')
      const bPv = sum(this.bAnalyticsData.daily, 'clickPv')
      const aValue = getPercentage(aPv, this.aPvTotal, 1)
      const bValue = getPercentage(bPv, this.bPvTotal, 1)
      return this.getCompareData(aValue, bValue)
    },
    getClickPvRateChartData () {
      const mapFn = (item) => {
        return getPercentage(item.clickPv, item.pv, 1)
      }
      return {
        a: this.aAnalyticsData.daily.map(mapFn),
        b: this.bAnalyticsData.daily.map(mapFn)
      }
    },
    getExitRateData () {
      const aValue = getPercentage(this.aPvTotal - this.aAnalyticsData.cvrExist, this.aPvTotal, 1)
      const bValue = getPercentage(this.bPvTotal - this.bAnalyticsData.cvrExist, this.bPvTotal, 1)
      return this.getCompareData(aValue, bValue)
    },
    getExitRateChartData () {
      const mapFn = (item) => {
        return getPercentage(item.pv - item.cvr, item.pv, 1)
      }
      return {
        a: this.aAnalyticsData.daily.map(mapFn),
        b: this.bAnalyticsData.daily.map(mapFn)
      }
    },
    getAvgDurationData () {
      const aSessionTotal = sum(this.aAnalyticsData.daily.map(item => item.sessTime * item.pv))
      const bSessionTotal = sum(this.bAnalyticsData.daily.map(item => item.sessTime * item.pv))
      const aValue = Number((aSessionTotal / this.aPvTotal / 1000).toFixed(1))
      const bValue = Number((bSessionTotal / this.bPvTotal / 1000).toFixed(1))
      return this.getCompareData(aValue, bValue)
    },
    getAvgDurationChartData () {
      const mapFn = (item) => {
        return Number((item.sessTime / 1000).toFixed(1))
      }
      return {
        a: this.aAnalyticsData.daily.map(mapFn),
        b: this.bAnalyticsData.daily.map(mapFn)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content__summary {
  margin-top: 70px;

  .title {
    font-size: 22px;
    font-weight: 600;
    color: #444444;
  }

  .box-layout {
    border-radius: 14px;
    background-color: #ffffff;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    padding: 25px;
  }

  .box__graph {
    margin-top: 30px;
    padding: 30px 40px;
    text-align: center;

    .button--wrap {
      display: inline-block;
      position: relative;
      text-align: center;
      white-space: nowrap;
      height: 38px;
      border-radius: 20px;
      border: 1px solid #e0e0e0;
      background-color: #fff;
      box-sizing: border-box;

      li {
        cursor: pointer;
        display: inline-block;
        box-sizing: border-box;
        height: 38px;
        line-height: 36px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        color: #888888;
        border-radius: 20px;
        padding-left: 15px;
        padding-right: 15px;

        &.on {
          position: relative;
          top: -1px;
          box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
          border: solid 1px #ec0147;
          background-color: #ec0147;
          color: #fff;
        }

        &:not(:nth-of-type(1)) {
          margin-left: -5px;
        }
      }
    }
  }
  .box__indicator {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;

    .box-layout {
      width: calc(50% - 15px);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .label {
        position: relative;
        display: inline-block;
        width: 38px;
        height: 38px;
        border-radius: 5px;
        margin-right: 22px;

        &.style1 {
          background-color: #9fce12;
        }
        &.style2 {
          background-color: #1199af;
        }

        .emphasis {
          @include positionXYcenter;
          font-size: 20px;
          font-weight: 600;
          color: #ffffff;
        }
      }

      .indicator--wrap {
        width: 40%;
        box-sizing: border-box;
        li {
          font-weight: 500;
          white-space: nowrap;
          margin-top: 12px;

          &:nth-of-type(1) {
            margin-top: 0;
          }
          &.largeA {
            .aValue {
              color: #7aa200;
            }
          }
          &.largeB {
            .bValue {
              color: #1199af;
            }
          }
          .tooltip {
            margin-right: 5px;
          }
          .value {
            float: right;
          }
        }
      }

      .divide {
        display: inline-block;
        width: 1px;
        height: 100%;
        border-right: 1px solid #d8d8d8;
      }
    }
  }
}
</style>
